<template>
  <v-app class="scan-qr-layout">
    <v-app-bar
      app
      flat
      height="65"
      class="qr-bar"
      :class="{ 'qr-bar--scrolled': scrolled }"
    >
      <div class="container-xl d-flex align-center px-0" style="gap: 10px">
        <ScanQrBarLogo :filename="store ? store.logo || '' : null" />

        <!-- Store info -->
        <div class="qr-bar__info flex-grow-1 min-width-0">
          <div class="qr-bar__store-name text-truncate">{{ store?.name }}</div>
          <div class="qr-bar__table-row">
            <span class="qr-bar__status-dot" aria-hidden="true" />
            <span v-if="isStoreScanMode" class="qr-bar__table-text">{{
              $t("store_buffet_menu")
            }}</span>
            <span v-else class="qr-bar__table-text"
              >{{ $t("table") }} {{ store?.table }}</span
            >
          </div>
        </div>

        <!-- Search button -->
        <v-btn
          v-if="buffetStage !== 'picker'"
          icon
          height="38"
          width="38"
          class="qr-icon-btn scan-touch-target"
          :aria-label="is_search ? $t('close') : $t('search')"
          @click="is_search = !is_search"
        >
          <v-icon size="17">fal fa-{{ is_search ? "times" : "search" }}</v-icon>
        </v-btn>

        <!-- Divider -->
        <div class="qr-bar__divider" aria-hidden="true" />

        <QrLanguageMenu />
      </div>
    </v-app-bar>

    <v-main class="scan-page-root">
      <div class="container-xl">
        <!-- Search row -->
        <v-row v-if="is_search && buffetStage !== 'picker'" class="mt-2">
          <v-col cols="12">
            <TTextField
              v-model="search"
              :placeholder="$t('search')"
              @keyup.enter.prevent="onSearch"
            >
              <template v-slot:append>
                <v-btn
                  icon
                  class="scan-touch-target"
                  :aria-label="$t('clear')"
                  @click="onClean"
                  v-if="search"
                >
                  <v-icon>fal fa-times</v-icon>
                </v-btn>
              </template>
              <template v-slot:append-outer>
                <v-btn
                  icon
                  color="primary"
                  class="mt-n1 scan-touch-target"
                  :aria-label="$t('search')"
                  @click="onSearch()"
                >
                  <v-icon>fal fa-search</v-icon>
                </v-btn>
              </template>
            </TTextField>
          </v-col>
        </v-row>

        <!-- Buffet plan picker (free table, BUFFET store) -->
        <BuffetPlanScanSheet
          v-if="isBuffetTable && buffetStage === 'picker'"
          :plans="buffetPlansList"
          :loading="buffetSheetLoading"
          :readonly="isReadOnlyBuffet"
          :readonly-notice="
            isStoreScanMode
              ? $t('store_scan_browse_only')
              : $t('please_ask_staff_to_open_table')
          "
          @select="onSelectPlanForPreview"
        />

        <!-- Session strip (buffet) -->
        <div
          v-if="heroVisible"
          class="qr-session-strip"
          role="region"
          :aria-label="$t('buffet_plans')"
        >
          <div class="qr-session-bullet">
            <div class="qr-session-bullet__icon" aria-hidden="true">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                <circle
                  cx="9"
                  cy="8"
                  r="3.5"
                  stroke="currentColor"
                  stroke-width="1.8"
                />
                <circle
                  cx="17"
                  cy="9"
                  r="2.8"
                  stroke="currentColor"
                  stroke-width="1.8"
                />
                <path
                  d="M3 19c0-3 2.5-5 6-5s6 2 6 5M14.5 19c.5-2 2.5-3.5 5-3.5s4 1 4.5 3"
                  stroke="currentColor"
                  stroke-width="1.8"
                  stroke-linecap="round"
                />
              </svg>
            </div>
            <div class="qr-session-bullet__text">
              <span class="qr-session-bullet__label">{{
                $t("buffet_persons")
              }}</span>
              <span class="qr-session-bullet__value">{{
                buffetHeroGuests
              }}</span>
            </div>
          </div>
          <div class="qr-session-divider" aria-hidden="true" />
          <div class="qr-session-bullet">
            <div class="qr-session-bullet__icon" aria-hidden="true">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                <path
                  d="M7 3v8a3 3 0 003 3v7M7 3v6M10 3v6M17 3c-1.5 0-3 1.5-3 4s1.5 4 3 4v10"
                  stroke="currentColor"
                  stroke-width="1.8"
                  stroke-linecap="round"
                />
              </svg>
            </div>
            <div class="qr-session-bullet__text">
              <span class="qr-session-bullet__label">{{
                $t("buffet_plans")
              }}</span>
              <span class="qr-session-bullet__value">
                {{
                  buffetHeroPlans
                    .map((p) => p.plan_name || p.planName)
                    .join(", ") || "—"
                }}
              </span>
            </div>
          </div>
          <div class="qr-session-divider" aria-hidden="true" />
          <div class="qr-session-bullet">
            <div class="qr-session-bullet__icon" aria-hidden="true">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                <circle
                  cx="12"
                  cy="12"
                  r="9"
                  stroke="currentColor"
                  stroke-width="1.8"
                />
                <path
                  d="M12 7v5l3 2"
                  stroke="currentColor"
                  stroke-width="1.8"
                  stroke-linecap="round"
                />
              </svg>
            </div>
            <div class="qr-session-bullet__text">
              <span class="qr-session-bullet__label">{{
                $t("session_started")
              }}</span>
              <span class="qr-session-bullet__value">{{
                buffetStartedTime || "—"
              }}</span>
            </div>
          </div>
        </div>

        <!-- Category strip (sticky) — hidden during plan picker -->
        <div
          v-if="buffetStage !== 'picker'"
          class="scan-category-sticky-wrap"
          :class="{ 'scan-category-sticky-wrap--no-hero': !heroVisible }"
        >
          <div class="container-xl px-0">
            <div class="scan-category-sticky-row">
              <v-sheet class="mb-2 transparent scan-category-sheet">
                <v-slide-group
                  v-model="value"
                  :show-arrows="$vuetify.breakpoint.mdAndUp"
                  class="scan-slide-group scan-category-strip"
                >
                  <v-slide-item
                    v-for="(item, index) in visibleCategories"
                    :key="index"
                    v-slot="{ active }"
                    :value="item.id"
                  >
                    <button
                      class="qr-cat-pill mx-1"
                      :class="{
                        'qr-cat-pill--active': active,
                        'qr-cat-pill--inactive': !active,
                      }"
                      role="tab"
                      :aria-selected="active ? 'true' : 'false'"
                      :aria-label="getCategoryName(item)"
                      @click="onCategorySelect(item.id)"
                    >
                      <span class="qr-cat-pill__label text-truncate">{{
                        getCategoryName(item)
                      }}</span>
                      <span
                        v-if="
                          item.id !== 0 && categoryCartCountMap[item.id] > 0
                        "
                        class="qr-cat-pill__count"
                        >{{ categoryCartCountMap[item.id] }}</span
                      >
                    </button>
                  </v-slide-item>
                </v-slide-group>
              </v-sheet>

              <div class="scan-layout-toggle" role="group" aria-label="Layout">
                <v-btn
                  icon
                  height="44"
                  width="44"
                  class="qr-icon-btn scan-touch-target"
                  :class="{
                    'scan-layout-toggle__btn--active': layoutMode === 'grid',
                  }"
                  aria-label="Grid view"
                  @click="setLayoutMode('grid')"
                >
                  <span class="scan-layout-toggle__icon">
                    <v-icon size="16">fal fa-th-large</v-icon>
                  </span>
                </v-btn>
                <v-btn
                  icon
                  height="44"
                  width="44"
                  class="qr-icon-btn scan-touch-target"
                  :class="{
                    'scan-layout-toggle__btn--active': layoutMode === 'list',
                  }"
                  aria-label="List view"
                  @click="setLayoutMode('list')"
                >
                  <span class="scan-layout-toggle__icon">
                    <v-icon size="16">fal fa-list</v-icon>
                  </span>
                </v-btn>
              </div>
            </div>
          </div>
        </div>

        <!-- Products -->
        <template v-if="buffetStage !== 'picker'">
          <div v-for="(item, i) in visibleProducts" :key="i">
            <div class="qr-section-header px-1 mb-3">
              <span class="qr-section-title">{{ getCategoryName(item) }}</span>
              <span class="qr-section-count"
                >{{ item.products ? item.products.length : 0 }}
                {{ $t("items") }}</span
              >
            </div>

            <masonry
              v-if="layoutMode === 'grid'"
              class="pt-1 scan-product"
              :cols="{ default: 3, 600: 2 }"
              :gutter="{ default: '12px', 600: '10px' }"
            >
              <div
                v-for="(data, index) in item.products"
                :key="
                  data.id + '/' + index + data.name + i + 1 + data.price_sale
                "
                class="body mb-3"
              >
                <v-card
                  elevation="0"
                  :tabindex="isPreviewing ? -1 : 0"
                  role="button"
                  :aria-disabled="isPreviewing ? 'true' : 'false'"
                  :aria-label="getProductName(data)"
                  class="qr-product-card fill-height cursor-pointer"
                  :class="{ 'qr-product-card--preview': isPreviewing }"
                  @click="handleProductClickGuarded(data)"
                  @keydown.enter="handleProductClickGuarded(data)"
                  @keydown.space.prevent="handleProductClickGuarded(data)"
                >
                  <div class="scan-product-card__media">
                    <!-- Gradient veil -->
                    <div class="qr-product-veil" aria-hidden="true" />

                    <!-- Included badge -->
                    <div
                      v-if="productIncludedInCategory(data, item)"
                      class="qr-product-badge--included"
                      aria-hidden="true"
                    >
                      <span class="qr-product-badge--included__dot" />
                      <span>{{ $t("included_in_plan") }}</span>
                    </div>

                    <template v-if="!isPreviewing">
                      <!-- Qty stepper (when in cart) -->
                      <div
                        v-if="cartQty(data.id) > 0"
                        class="qr-product-qty-stepper"
                        aria-hidden="true"
                      >
                        <button
                          type="button"
                          class="qr-product-qty-stepper__btn"
                          @click.stop="removeOneFromCart(data.id)"
                        >
                          −
                        </button>
                        <span class="qr-product-qty-stepper__count">{{
                          cartQty(data.id)
                        }}</span>
                        <button
                          type="button"
                          class="qr-product-qty-stepper__btn qr-product-qty-stepper__btn--plus"
                          @click.stop="handleProductClick(data)"
                        >
                          +
                        </button>
                      </div>

                      <!-- Add button (when not in cart) -->
                      <button
                        v-else
                        type="button"
                        class="scan-product-add-pill"
                        :aria-label="`${getProductName(data)} — ${$t('add')}`"
                        @click.stop="handleProductClick(data)"
                      >
                        <v-icon size="16" color="white">fal fa-plus</v-icon>
                      </button>
                    </template>

                    <ScanProductImage
                      :img="data.img"
                      :contain="true"
                      :rounded="true"
                      :fill-parent="true"
                    />
                  </div>

                  <div class="scan-product-card__body">
                    <div
                      class="scan-product-card__title"
                      :title="getProductName(data)"
                    >
                      {{ getProductName(data) }}
                    </div>
                    <div
                      v-if="Number(value) === 0"
                      class="scan-product-card__category"
                      :title="getCategoryName(item)"
                    >
                      {{ getCategoryName(item) }}
                    </div>
                    <div class="scan-product-card__price mt-1">
                      <span
                        class="qr-product-price"
                        :class="{
                          'qr-product-price--included':
                            productIncludedInCategory(data, item),
                        }"
                      >
                        {{ getCurrencySymbol(data)
                        }}{{ data.price_sale | NumberFormat }}
                      </span>
                    </div>
                  </div>
                </v-card>
              </div>
            </masonry>

            <div v-else class="scan-product-list">
              <div
                v-for="(data, index) in item.products"
                :key="'list/' + data.id + '/' + index"
                class="scan-product-row"
                :class="{ 'scan-product-row--preview': isPreviewing }"
                :tabindex="isPreviewing ? -1 : 0"
                role="button"
                :aria-disabled="isPreviewing ? 'true' : 'false'"
                :aria-label="getProductName(data)"
                @click="handleProductClickGuarded(data)"
                @keydown.enter="handleProductClickGuarded(data)"
                @keydown.space.prevent="handleProductClickGuarded(data)"
              >
                <div class="scan-product-row__media">
                  <ScanProductImage
                    :img="data.img"
                    :contain="true"
                    :rounded="true"
                  />
                </div>

                <div class="scan-product-row__main">
                  <div
                    class="scan-product-row__title"
                    :title="getProductName(data)"
                  >
                    {{ getProductName(data) }}
                  </div>
                  <div
                    v-if="Number(value) === 0"
                    class="scan-product-row__category"
                    :title="getCategoryName(item)"
                  >
                    {{ getCategoryName(item) }}
                  </div>
                  <div class="scan-product-row__price">
                    <span
                      class="qr-product-price"
                      :class="{
                        'qr-product-price--included': productIncludedInCategory(
                          data,
                          item
                        ),
                      }"
                    >
                      {{ getCurrencySymbol(data)
                      }}{{ data.price_sale | NumberFormat }}
                    </span>
                  </div>
                </div>

                <div class="scan-product-row__actions">
                  <div
                    v-if="!isPreviewing && cartQty(data.id) > 0"
                    class="qr-product-qty-stepper qr-product-qty-stepper--row"
                    aria-hidden="true"
                  >
                    <button
                      type="button"
                      class="qr-product-qty-stepper__btn"
                      @click.stop="removeOneFromCart(data.id)"
                    >
                      −
                    </button>
                    <span class="qr-product-qty-stepper__count">{{
                      cartQty(data.id)
                    }}</span>
                    <button
                      type="button"
                      class="qr-product-qty-stepper__btn qr-product-qty-stepper__btn--plus"
                      @click.stop="handleProductClick(data)"
                    >
                      +
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </template>

        <!-- Empty-state in preview (plan has zero matching product groups) -->
        <div
          v-if="isPreviewing && !overlay && visibleProducts.length === 0"
          class="buffet-preview-empty"
        >
          <v-icon size="32" color="grey">fal fa-utensils</v-icon>
          <p class="buffet-preview-empty__text">
            {{ $t("preview_plan_items") }}
          </p>
        </div>
      </div>
    </v-main>

    <ScanBottomNav v-if="buffetStage === 'done' && !isStoreScanMode" />

    <!-- Read-only sticky notice when customer self-open is disabled (picker stage) -->
    <div
      v-if="isReadOnlyBuffet && buffetStage === 'picker'"
      class="buffet-readonly-banner"
      role="status"
      :aria-label="
        isStoreScanMode
          ? $t('store_scan_browse_only')
          : $t('please_ask_staff_to_open_table')
      "
    >
      <v-icon size="20" class="buffet-readonly-banner__icon"
        >fal fa-info-circle</v-icon
      >
      <span class="buffet-readonly-banner__text">{{
        isStoreScanMode
          ? $t("store_scan_browse_only")
          : $t("please_ask_staff_to_open_table")
      }}</span>
    </div>

    <BuffetSetupBottomBar
      v-if="buffetStage === 'preview' && previewPlan"
      :plan="previewPlan"
      :loading="buffetSheetLoading"
      :readonly="isReadOnlyBuffet"
      @back="onBackToPicker"
      @confirm="onConfirmPreview"
    />

    <app-loading :overlay="overlay" />

    <!-- Product Selection Modal -->
    <ProductSelectionModal
      v-if="selectedProduct"
      :show.sync="showVariationModal"
      :product="selectedProduct"
      @update:show="handleCloseVariationModal"
    />
    <!-- Pack Quantity Modal -->
    <PackQuantityModal
      v-if="selectedProductForPack"
      :show.sync="showPackModal"
      :product="selectedProductForPack"
      @update:show="handleClosePackModal"
    />
  </v-app>
</template>
<script>
import { myMixin } from "@/mixin/main";
import { mapGetters, mapMutations } from "vuex";
import { mainStore } from "@/stores/modules/cart";
import Cookies from "js-cookie";
import ProductSelectionModal from "@/components/products/ProductSelectionModal";
import PackQuantityModal from "@/components/products/PackQuantityModal.vue";
import TTextField from "@/components/global/TextField";
import BuffetPlanScanSheet from "@/components/scan/BuffetPlanScanSheet.vue";
import BuffetSetupBottomBar from "@/components/scan/BuffetSetupBottomBar.vue";
import ScanProductImage from "@/components/scan/ScanProductImage.vue";
import ScanBottomNav from "@/components/scan/ScanBottomNav.vue";
import ScanQrBarLogo from "@/components/scan/ScanQrBarLogo.vue";
import QrLanguageMenu from "@/components/qr_order/QrLanguageMenu.vue";

export default {
  mixins: [myMixin],
  components: {
    ProductSelectionModal,
    PackQuantityModal,
    TTextField,
    BuffetPlanScanSheet,
    BuffetSetupBottomBar,
    ScanProductImage,
    ScanBottomNav,
    ScanQrBarLogo,
    QrLanguageMenu,
  },
  data: () => ({
    defineStore: mainStore(),
    is_search: false,
    value: 0,
    search: "",
    branch_id: "",
    table_id: "",
    store_code: null,
    store: null,
    category_id: null,
    categories: [],
    products: [],
    showVariationModal: false,
    selectedProduct: null,
    showPackModal: false,
    selectedProductForPack: null,
    store_type_code: "",
    buffetStatus: null,
    buffetPlansList: [],
    /** 'picker' (BUFFET free table, plans available) | 'preview' (plan chosen, not yet confirmed) | 'done' (normal flow). */
    buffetStage: "done",
    /** Currently previewed plan object (when buffetStage === 'preview'). */
    previewPlan: null,
    buffetSheetLoading: false,
    // New visual state
    scrolled: false,
    buffetStartedAt: null,
    layoutMode: "list",
  }),
  mounted() {
    this.SET_OVERLAY(true);
    const { store_id, branch_id, table_id } = this.$route.params;
    this.loadLayoutMode();
    this.initLocation(store_id, branch_id, table_id);
    this._onScroll = () => {
      this.scrolled = window.scrollY > 8;
    };
    window.addEventListener("scroll", this._onScroll, { passive: true });
  },
  beforeDestroy() {
    window.removeEventListener("scroll", this._onScroll);
  },
  watch: {
    value(val) {
      if (val === undefined || val === null || val === "") {
        this.value = 0;
        return;
      }
      if (this.buffetStage === "picker") return;
      this.fetch_product();
    },
  },
  computed: {
    ...mapGetters({
      overlay: "index/overlay",
      locales: "auth/locales",
    }),
    isBuffetTable() {
      return this.store_type_code === "BUFFET";
    },
    isStoreScanMode() {
      const id = this.table_id || (this.$route.params && this.$route.params.table_id);
      return String(id || "").toLowerCase() === "store";
    },
    customerCanSelfOpen() {
      // Default to false when unknown so we fail safe on the staff-only side.
      return !!(
        this.store &&
        (this.store.allow_customer_open_table === true ||
          this.store.allow_customer_open_table === 1)
      );
    },
    showQrOutsideBuffetPlan() {
      return !!(
        this.store &&
        (this.store.show_qr_outside_buffet_plan === true ||
          this.store.show_qr_outside_buffet_plan === 1)
      );
    },
    isReadOnlyBuffet() {
      if (this.isStoreScanMode) {
        return (
          this.buffetStage === "picker" || this.buffetStage === "preview"
        );
      }
      // Read-only browsing for buffet free tables while owner keeps self-open disabled.
      return (
        this.isBuffetTable &&
        !this.customerCanSelfOpen &&
        (this.buffetStage === "picker" || this.buffetStage === "preview")
      );
    },
    isPreviewing() {
      return this.buffetStage === "preview" && !!this.previewPlan;
    },
    previewCategoryIdSet() {
      if (!this.isPreviewing) return new Set();
      const ids = Array.isArray(this.previewPlan?.included_category_ids)
        ? this.previewPlan.included_category_ids
        : [];
      return new Set(ids.map(Number));
    },
    /** When set, QR menu is limited to these category ids (preview or live buffet). */
    buffetMenuCategoryIdSet() {
      if (this.isPreviewing) {
        return this.previewCategoryIdSet;
      }
      if (this.showQrOutsideBuffetPlan || !this.buffetStatus?.buffet_active) {
        return null;
      }
      const set = new Set();
      (this.buffetStatus.buffet_plans || []).forEach((row) => {
        (row.included_category_ids || []).forEach((id) => {
          set.add(Number(id));
        });
      });
      return set.size > 0 ? set : null;
    },
    visibleCategories() {
      const set = this.buffetMenuCategoryIdSet;
      if (!set) {
        return this.categories;
      }
      return (this.categories || []).filter(
        (c) => Number(c.id) === 0 || set.has(Number(c.id)) || c.is_buffet_included === true
      );
    },
    visibleProducts() {
      const set = this.buffetMenuCategoryIdSet;
      if (!set) {
        return this.products;
      }
      return (this.products || []).filter((cat) => set.has(Number(cat.id)) || cat.is_buffet_included === true);
    },
    showBuffetHero() {
      return (
        this.isBuffetTable &&
        this.buffetStatus &&
        Array.isArray(this.buffetStatus.buffet_plans) &&
        this.buffetStatus.buffet_plans.length > 0
      );
    },
    heroVisible() {
      return (
        this.isBuffetTable &&
        this.showBuffetHero &&
        this.buffetStage === "done"
      );
    },
    buffetHeroPlans() {
      return this.buffetStatus?.buffet_plans || [];
    },
    buffetHeroGuests() {
      return this.buffetHeroPlans.reduce(
        (s, r) => s + Number(r.persons || 0),
        0
      );
    },
    categoryIncludedMap() {
      const m = {};
      (this.products || []).forEach((cat) => {
        m[cat.id] = !!cat.is_buffet_included;
      });
      return m;
    },
    buffetStartedTime() {
      if (!this.buffetStartedAt) return "";
      return this.buffetStartedAt.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      });
    },
    cartCurrencySymbol() {
      const cart = this.defineStore.cart || [];
      if (!cart.length) return "₭";
      return (
        cart[0]?.currency?.symbol || cart[0]?.item?.currency?.symbol || "₭"
      );
    },
    categoryCartCountMap() {
      const m = {};
      (this.defineStore.cart || []).forEach((line) => {
        const catId = line.item?.category_id || line.item?.category?.id;
        if (catId) {
          m[catId] = (m[catId] || 0) + Number(line.qty || 0);
        }
        // "All" category key 0
        m[0] = (m[0] || 0) + Number(line.qty || 0);
      });
      return m;
    },
  },

  methods: {
    ...mapMutations({
      SET_OVERLAY: "index/SET_OVERLAY",
    }),
    getCurrencySymbol(item) {
      return item && item.currency && item.currency.symbol
        ? item.currency.symbol
        : "₭";
    },
    productIncluded(data) {
      return data.is_buffet_included === 1 || data.is_buffet_included === true;
    },
    productIncludedInCategory(data, category) {
      if (this.isPreviewing) {
        const cid = Number(category?.id ?? data?.category_id ?? 0);
        return this.previewCategoryIdSet.has(cid);
      }
      return this.productIncluded(data);
    },
    handleProductClickGuarded(item) {
      if (this.isPreviewing) return;
      this.handleProductClick(item);
    },
    cartQty(productId) {
      const id = Number(productId);
      if (!id) return 0;
      const line = (this.defineStore.cart || []).find(
        (e) => Number(e.id) === id
      );
      return line ? Number(line.qty || 0) : 0;
    },
    removeOneFromCart(productId) {
      const id = Number(productId);
      this.defineStore.decrementCart({ payload: { id } });
    },
    async loadBuffetStatus() {
      try {
        const { data } = await this.$http.get("/qr/buffet_status");
        if (data.status && data.data) {
          this.buffetStatus = data.data;
          if (!this.buffetStartedAt) {
            this.buffetStartedAt = new Date();
          }
          const lines = (data.data.buffet_plans || []).map((row) => ({
            buffet_plan_id: row.buffet_plan_id,
            plan_name: row.plan_name,
            unit_price: row.unit_price,
            persons: row.persons,
            subtotal: row.subtotal,
            included_category_ids: row.included_category_ids,
          }));
          this.defineStore.setBuffetPlanLines(lines);
        }
      } catch (e) {
        console.error(e);
      }
    },
    async loadBuffetPlansList() {
      try {
        const { data } = await this.$http.get("/qr/buffet_plans");
        if (data.status) {
          this.buffetPlansList = Array.isArray(data.data) ? data.data : [];
        }
      } catch (e) {
        console.error(e);
        this.buffetPlansList = [];
      }
    },
    async onBuffetSetupDone({ buffet_plan_id, persons }) {
      try {
        this.SET_OVERLAY(true);
        this.buffetSheetLoading = true;
        await this.$http.post("/qr/buffet_setup", {
          buffet_plan_id,
          persons,
        });
        await this.loadBuffetStatus();
        this.buffetStage = "done";
        this.previewPlan = null;
        this.value = 0;
        await this.fetch_product();
      } catch (e) {
        console.error(e);
      } finally {
        this.buffetSheetLoading = false;
        this.SET_OVERLAY(false);
      }
    },
    onSelectPlanForPreview(plan) {
      if (!plan) return;
      this.previewPlan = plan;
      this.buffetStage = "preview";
      this.value = 0;
      this.fetch_product();
    },
    onBackToPicker() {
      this.previewPlan = null;
      this.buffetStage = "picker";
    },
    async onConfirmPreview({ buffet_plan_id, persons }) {
      // Defense-in-depth: server also rejects (403 customer_open_table_disabled).
      if (!this.customerCanSelfOpen) return;
      await this.onBuffetSetupDone({ buffet_plan_id, persons });
    },
    async initLocation(store_id, branch_id, table_id) {
      const data = await this.checkLocation(store_id, branch_id, table_id);
      if (data.status && data.data && data.data.token) {
        this.$http.defaults.headers.common[
          "Authorization"
        ] = `Bearer ${data.data.token}`;
        Cookies.set("token", data.data.token);
        Cookies.set("isLoggedIn", true);
        localStorage.setItem("store_id", store_id);
        localStorage.setItem("branch_id", branch_id);
        localStorage.setItem("table_id", table_id);
        this.store = data.data;
        try {
          if (data.data.logo) {
            localStorage.setItem("scan_store_logo", data.data.logo);
          } else {
            localStorage.removeItem("scan_store_logo");
          }
        } catch (e) {
          // ignore
        }
        this.store_code = store_id;
        this.branch_id = branch_id;
        this.table_id = table_id;
        this.store_type_code = data.data.store_type_code || "";
        localStorage.setItem("scan_store_type_code", this.store_type_code);

        await this.fetch_category();

        if (this.isStoreScanMode && this.store_type_code !== "BUFFET") {
          this.SET_OVERLAY(false);
          this.$router.push("/");
          return;
        }

        if (this.store_type_code === "BUFFET") {
          if (this.isStoreScanMode) {
            await this.loadBuffetPlansList();
            this.buffetStage = "picker";
            this.previewPlan = null;
            this.SET_OVERLAY(false);
            return;
          }

          await this.loadBuffetStatus();
          if (!this.buffetStatus?.buffet_active) {
            await this.loadBuffetPlansList();
            if (this.buffetPlansList.length) {
              this.buffetStage = "picker";
              this.previewPlan = null;
              this.SET_OVERLAY(false);
              return;
            }
          }
          this.buffetStage = "done";
        } else {
          this.defineStore.setBuffetPlanLines([]);
          this.buffetStage = "done";
        }

        await this.fetch_product();
        return;
      }
      console.error("Location check failed:", data.message || "Unknown error");
      this.$router.push("/");
    },
    async onClean() {
      this.search = "";
      await this.fetch_product();
    },
    async onSearch() {
      if (this.search === "") return;
      await this.fetch_product();
    },

    async fetch_category() {
      try {
        const { data } = await this.$http.get("/qr/categories");
        const append = {
          id: 0,
          la_name: "ທັງໝົດ",
          en_name: "All",
          th_name: "ทั้งหมด",
          cn_name: "全部",
        };
        this.categories = [append, ...data.data];
      } catch (error) {
        console.log(error);
      }
    },
    async fetch_product() {
      try {
        this.SET_OVERLAY(true);
        const categoryId = this.value ?? 0;
        const params = `search=${this.search}&category_id=${categoryId}&store_id=${this.store_code}&branch_id=${this.branch_id}`;
        const { data } = await this.$http.get(`/qr/products?${params}`);
        this.products = data.data;
      } catch (error) {
        console.log(error);
      } finally {
        this.SET_OVERLAY(false);
      }
    },
    handleProductClick(item) {
      if (
        item.has_variations &&
        item.variations &&
        item.variations.length > 0
      ) {
        this.selectedProduct = item;
        this.showVariationModal = true;
      } else if (item.size && item.size.unit === "pack") {
        this.selectedProductForPack = item;
        this.showPackModal = true;
      } else {
        this.addToCart(item);
      }
    },

    handleCloseVariationModal() {
      this.showVariationModal = false;
      this.selectedProduct = null;
    },

    handleClosePackModal() {
      this.showPackModal = false;
      this.selectedProductForPack = null;
    },

    async addToCart(item) {
      const body = {
        that: this,
        payload: item,
      };
      this.defineStore.addToCart(body);
    },
    onCategorySelect(categoryId) {
      this.value = categoryId;
    },
    loadLayoutMode() {
      try {
        const v = localStorage.getItem("scan_layout_mode");
        if (v === "grid" || v === "list") {
          this.layoutMode = v;
        }
      } catch (e) {
        // ignore
      }
    },
    setLayoutMode(mode) {
      this.layoutMode = mode === "list" ? "list" : "grid";
      try {
        localStorage.setItem("scan_layout_mode", this.layoutMode);
      } catch (e) {
        // ignore
      }
    },
    changeLocale(code) {
      localStorage.setItem("locale", code);
      this.$i18n.locale = code;
    },
  },
};
</script>

<style scoped>
main.v-main.scan-page-root {
  background-color: var(--qr-bg);
  min-height: 100%;
  touch-action: manipulation;
  /* Keep content above bottom nav (ScanBottomNav) + iOS safe area */
  padding-bottom: calc(80px + env(safe-area-inset-bottom, 0px)) !important;
}

/* ── App bar (aligned with cart.vue / receipt.vue) ───────── */
.qr-bar {
  background: var(--qr-appbar-tint-clear) !important;
  transition: background 0.2s, border-color 0.2s;
  border-bottom: 0.5px solid transparent !important;
  box-shadow: none !important;
}

.qr-bar--scrolled {
  background: var(--qr-appbar-tint-scrolled) !important;
  -webkit-backdrop-filter: blur(18px) saturate(170%);
  backdrop-filter: blur(18px) saturate(170%);
  border-bottom-color: rgba(40, 28, 16, 0.07) !important;
}

.qr-bar ::v-deep .v-toolbar__content {
  background: transparent !important;
}

.qr-bar__logo {
  width: 38px;
  height: 38px;
  border-radius: 11px;
  flex-shrink: 0;
  background: linear-gradient(135deg, #5b39c9 0%, #4529a8 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: inset 0 0 0 0.5px rgba(255, 255, 255, 0.08),
    0 4px 10px rgba(0, 0, 0, 0.18);
}

.qr-bar__info {
  line-height: 1;
}

.qr-bar__store-name {
  font-size: 14px;
  font-weight: 700;
  color: var(--qr-text);
  letter-spacing: -0.2px;
  line-height: 1.15;
}

.qr-bar__table-row {
  display: flex;
  align-items: center;
  gap: 5px;
  margin-top: 2px;
}

.qr-bar__status-dot {
  width: 5px;
  height: 5px;
  border-radius: 999px;
  background: var(--qr-included-fg);
  flex-shrink: 0;
}

.qr-bar__table-text {
  font-size: 11px;
  color: var(--qr-muted);
  font-variant-numeric: tabular-nums;
}

.qr-icon-btn {
  background: rgba(40, 28, 16, 0.05) !important;
  border-radius: 999px !important;
}

.qr-bar__divider {
  height: 24px;
  width: 1px;
  background: rgba(40, 28, 16, 0.08);
  margin: 0 2px;
  flex-shrink: 0;
}

.qr-lang-btn {
  display: flex;
  align-items: center;
  gap: 4px;
  height: 32px;
  padding: 0 9px;
  border-radius: 999px;
  border: 0;
  background: transparent;
  color: var(--qr-muted);
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.4px;
  cursor: pointer;
  font-family: inherit;
  min-width: 44px;
  min-height: 44px;
}

/* ── Session strip ─────────────────────────────────────── */
.qr-session-strip {
  margin: 4px 0 14px;
  padding: 10px 12px;
  border-radius: 14px;
  background: rgba(40, 28, 16, 0.035);
  border: 0.5px solid rgba(40, 28, 16, 0.06);
  display: flex;
  align-items: center;
  gap: 10px;
}

.qr-session-divider {
  width: 1px;
  height: 28px;
  background: rgba(40, 28, 16, 0.08);
  flex-shrink: 0;
}

.qr-session-bullet {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
  overflow: hidden;
}

.qr-session-bullet__icon {
  width: 28px;
  height: 28px;
  border-radius: 8px;
  flex-shrink: 0;
  background: rgba(40, 28, 16, 0.05);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--qr-text);
}

.qr-session-bullet__text {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.qr-session-bullet__label {
  font-size: 9.5px;
  color: var(--qr-muted);
  text-transform: uppercase;
  letter-spacing: 0.6px;
  font-weight: 600;
  white-space: nowrap;
}

.qr-session-bullet__value {
  font-size: 12.5px;
  color: var(--qr-text);
  font-weight: 600;
  line-height: 1.2;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* ── Category sticky ───────────────────────────────────── */
.scan-category-sticky-wrap {
  position: sticky;
  top: 64px;
  z-index: 5;
  width: 100vw;
  margin-left: calc(50% - 50vw);
  margin-right: calc(50% - 50vw);
  background-color: #ffffff;
  border-bottom: 1px solid rgba(40, 28, 16, 0.07);
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.06);
  box-sizing: border-box;
  padding-bottom: 6px;
  margin-bottom: 8px;
}

/* Add breathing room above the strip when the buffet hero strip is hidden
   (RESTAURANT store, BUFFET preview, or BUFFET free table without active plan). */
.scan-category-sticky-wrap--no-hero {
  margin-top: 0;
  padding-top: 6px;
}

.scan-category-sticky-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.scan-category-sheet {
  flex: 1 1 auto;
  min-width: 0;
}

.scan-layout-toggle {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  flex: 0 0 auto;
}

.scan-layout-toggle__btn--active {
  background: rgba(40, 28, 16, 0.06) !important;
}

.scan-layout-toggle__icon {
  width: 34px;
  height: 34px;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.scan-category-sheet ::v-deep .v-slide-group__content {
  padding-block: 6px;
}

/* Category pills */
.qr-cat-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 34px;
  min-height: 40px;
  padding: 0 14px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: -0.1px;
  border: 1px solid transparent;
  cursor: pointer;
  font-family: inherit;
  white-space: nowrap;
  transition: background 0.18s, color 0.18s, box-shadow 0.18s, border-color 0.18s;
  touch-action: manipulation;
}

.qr-cat-pill--active {
  background: var(--qr-accent);
  color: #fff;
  border-color: var(--qr-accent);
  box-shadow: 0 3px 10px rgba(67, 142, 242, 0.32), 0 1px 3px rgba(67, 142, 242, 0.18);
}

.qr-cat-pill--inactive {
  background: #ffffff;
  color: var(--qr-text);
  border-color: rgba(40, 28, 16, 0.13);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.07);
}

.qr-cat-pill--inactive:hover {
  background: #f5f8ff;
  border-color: var(--qr-accent-border);
}

.qr-cat-pill__label {
  max-width: 140px;
  display: inline-block;
  vertical-align: middle;
}

.qr-cat-pill__count {
  font-size: 10.5px;
  font-weight: 700;
  padding: 1px 6px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.22);
  color: #fff;
  font-variant-numeric: tabular-nums;
}

.qr-cat-pill--inactive .qr-cat-pill__count {
  background: var(--qr-accent-soft);
  color: var(--qr-accent);
}

.scan-product-card__category {
  margin-top: 2px;
  font-size: 11px;
  line-height: 1.2;
  color: var(--qr-muted);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.qr-product-price--included {
  color: var(--qr-included-fg);
  font-weight: 700;
}

.scan-product-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding-top: 4px;
}

.scan-product-row {
  display: grid;
  grid-template-columns: 68px 1fr auto;
  align-items: center;
  gap: 12px;
  padding: 10px;
  border-radius: 16px;
  background: var(--qr-card-bg);
  border: 0.5px solid var(--qr-card-border);
  cursor: pointer;
}

.scan-product-row:focus-visible {
  outline: 2px solid var(--qr-accent);
  outline-offset: 2px;
}

.scan-product-row__media {
  width: 68px;
  height: 68px;
  border-radius: 14px;
  overflow: hidden;
  position: relative;
}

.scan-product-row__main {
  min-width: 0;
}

.scan-product-row__title {
  font-weight: 800;
  font-size: 13.5px;
  color: var(--qr-text);
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.scan-product-row__category {
  margin-top: 2px;
  font-size: 11px;
  line-height: 1.2;
  color: var(--qr-muted);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.scan-product-row__price {
  margin-top: 4px;
}

.scan-product-row__actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  min-width: 132px;
}

.qr-product-qty-stepper--row {
  position: static !important;
  bottom: auto !important;
  right: auto !important;
  z-index: auto !important;
  height: 44px;
  padding: 2px 6px;
}

.qr-product-qty-stepper--row .qr-product-qty-stepper__count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 22px;
}

/* Preview-mode (buffet plan picker -> preview) */
.qr-product-card--preview,
.scan-product-row--preview {
  cursor: default;
}

.buffet-preview-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: calc(100vh - 220px);
  padding: 24px 16px 120px;
  color: var(--qr-muted);
  text-align: center;
}

.buffet-preview-empty__text {
  margin: 0;
  font-size: 13px;
  font-weight: 600;
}

/* ── Read-only buffet notice (sticky bottom banner during picker) ──── */
.buffet-readonly-banner {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 6;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 16px calc(12px + env(safe-area-inset-bottom));
  background: var(--qr-card-bg, #fff);
  border-top: 1px solid var(--qr-card-border, rgba(0, 0, 0, 0.08));
  box-shadow: 0 -4px 16px rgba(0, 0, 0, 0.06);
  color: var(--qr-text, #1f2937);
  text-align: center;
}

.buffet-readonly-banner__icon {
  color: var(--qr-accent);
}

.buffet-readonly-banner__text {
  font-size: 13px;
  font-weight: 600;
  line-height: 1.35;
}

/* ── Hero text ──────────────────────────────────────────── */

/* ── Section headers ────────────────────────────────────── */
.qr-section-header {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  margin-top: 16px;
}

.qr-section-title {
  font-size: 17px;
  font-weight: 700;
  color: var(--qr-text);
  letter-spacing: -0.4px;
}

.qr-section-count {
  font-size: 11px;
  color: var(--qr-muted);
  font-weight: 600;
  font-variant-numeric: tabular-nums;
}

/* ── Product cards ──────────────────────────────────────── */
.qr-product-card {
  border: 0.5px solid var(--qr-card-border) !important;
  border-radius: 18px !important;
  background: var(--qr-card-bg) !important;
  box-shadow: 0 6px 18px rgba(40, 28, 16, 0.06),
    0 1px 0 rgba(255, 255, 255, 0.8) inset;
  transition: transform 0.18s cubic-bezier(0.3, 0.8, 0.4, 1.4), box-shadow 0.2s;
  cursor: pointer;
  overflow: hidden;
}

.qr-product-card:hover {
  box-shadow: 0 10px 28px rgba(40, 28, 16, 0.1);
  z-index: 2;
}

.qr-product-card:focus-visible {
  outline: 2px solid var(--qr-accent);
  outline-offset: 2px;
  z-index: 2;
}

.scan-product-card__media {
  position: relative;
  aspect-ratio: 4 / 3;
  border-radius: 18px 18px 0 0;
  overflow: hidden;
  background: linear-gradient(145deg, var(--qr-accent-faint) 0%, var(--qr-accent-ghost) 100%);
}

.scan-product-card__media >>> .v-image {
  height: 100% !important;
}

/* Gradient veil */
.qr-product-veil {
  position: absolute;
  inset: 0;
  pointer-events: none;
  background: linear-gradient(
    180deg,
    rgba(0, 0, 0, 0) 55%,
    rgba(0, 0, 0, 0.15) 100%
  );
  z-index: 1;
}

/* Included badge */
.qr-product-badge--included {
  position: absolute;
  top: 10px;
  left: 10px;
  z-index: 3;
  padding: 4px 8px;
  border-radius: 999px;
  background: var(--qr-included-bg);
  -webkit-backdrop-filter: blur(8px);
  backdrop-filter: blur(8px);
  color: var(--qr-included-fg);
  font-size: 9.5px;
  font-weight: 700;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  border: 0.5px solid rgba(79, 122, 68, 0.2);
  display: flex;
  align-items: center;
  gap: 4px;
}

.qr-product-badge--included__dot {
  width: 4px;
  height: 4px;
  border-radius: 999px;
  background: var(--qr-included-fg);
  flex-shrink: 0;
}

/* Qty stepper overlay */
.qr-product-qty-stepper {
  position: absolute;
  bottom: 10px;
  right: 8px;
  z-index: 4;
  display: flex;
  align-items: center;
  background: #fff;
  -webkit-backdrop-filter: blur(10px);
  backdrop-filter: blur(10px);
  border: 1px solid var(--qr-accent);
  border-radius: 999px;
  padding: 2px;
  box-shadow: var(--qr-stepper-shadow);
}

.qr-product-qty-stepper__btn {
  width: 22px;
  height: 22px;
  min-width: 36px;
  min-height: 36px;
  border-radius: 999px;
  border: 0;
  background: transparent;
  color: var(--qr-text);
  font-size: 16px;
  line-height: 1;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
}

.qr-product-qty-stepper__btn--plus {
  background: var(--qr-accent);
  color: #fff;
  font-weight: 600;
}

.qr-product-qty-stepper__count {
  min-width: 18px;
  text-align: center;
  font-size: 13px;
  font-weight: 700;
  color: var(--qr-accent);
  font-variant-numeric: tabular-nums;
}

/* Add button */
.scan-product-add-pill {
  position: absolute;
  bottom: 10px;
  right: 10px;
  z-index: 4;
  width: 32px;
  height: 32px;
  min-width: 44px;
  min-height: 44px;
  border: 0 !important;
  border-radius: 999px;
  background: var(--qr-accent);
  color: #fff;
  line-height: 1;
  box-shadow: var(--qr-control-shadow);
  cursor: pointer;
  touch-action: manipulation;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
}

.scan-product-add-pill:focus-visible {
  outline: 2px solid var(--qr-accent);
  outline-offset: 2px;
}

/* Card body */
.scan-product-card__body {
  padding: 8px 10px 10px;
}

.scan-product-card__title {
  font-size: 13px;
  font-weight: 600;
  letter-spacing: -0.1px;
  color: var(--qr-text);
  line-height: 1.3;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  /* min-height: calc(13px * 1.3 * 2); */
}

.qr-product-price {
  font-size: 14px;
  font-weight: 700;
  color: var(--qr-accent);
  font-variant-numeric: tabular-nums;
  letter-spacing: -0.2px;
}

/* Shared utilities */
.scan-touch-target {
  min-width: 44px !important;
  min-height: 44px !important;
}

.min-width-0 {
  min-width: 0;
}
.cursor-pointer {
  cursor: pointer;
}

@media (prefers-reduced-motion: reduce) {
  .qr-product-card,
  .scan-product-add-pill,
  .qr-cat-pill {
    transition: none !important;
  }
}

@media (max-width: 959px) {
  .scan-slide-group ::v-deep .v-slide-group__prev,
  .scan-slide-group ::v-deep .v-slide-group__next {
    display: none !important;
  }
}
</style>
